import { useState } from "react";
import {
  useDeleteCaption,
  useUploadCaption,
  useVideoCaptions,
} from "../../lib/youtube";

/**
 * Captions sub-section of the video edit page.
 *
 * Lists existing tracks (auto-generated + manually-uploaded), lets the
 * editor upload a new .srt or .vtt with a language code + name +
 * draft toggle, and delete tracks.
 *
 * Quota: list 50, upload 400, delete 50. Surfaced as small italic
 * captions next to each action so the editor isn't surprised when their
 * daily total ticks up.
 */

const COMMON_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: "en", label: "English" },
  { code: "hr", label: "Hrvatski (Croatian)" },
  { code: "sl", label: "Slovenščina (Slovenian)" },
  { code: "sa", label: "संस्कृत (Sanskrit)" },
  { code: "hi", label: "हिन्दी (Hindi)" },
  { code: "ta", label: "தமிழ் (Tamil)" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
];

export function CaptionsManager({ videoId }: { videoId: string }) {
  const q = useVideoCaptions(videoId);
  const upload = useUploadCaption(videoId);
  const del = useDeleteCaption(videoId);

  const [file, setFile] = useState<File | null>(null);
  const [language, setLanguage] = useState("en");
  const [name, setName] = useState("");
  const [isDraft, setIsDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const captions = q.data?.captions ?? [];

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError("Pick a .srt or .vtt file first.");
      return;
    }
    if (file.size > 1024 * 1024) {
      setError("Caption files must be 1 MB or smaller.");
      return;
    }
    try {
      await upload.mutateAsync({ file, language, name, isDraft });
      setFile(null);
      setName("");
      setIsDraft(false);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Couldn't upload that caption track.",
      );
    }
  };

  return (
    <section className="yt-form-section">
      <header className="yt-form-section-head">
        <p className="page-eyebrow">Captions</p>
        <p className="font-grotesk text-[0.62rem] text-[var(--color-ink-mute)] tracking-[0.04em]">
          400 quota / upload
        </p>
      </header>

      {q.isLoading ? (
        <p className="yt-admin-empty">Loading captions…</p>
      ) : captions.length === 0 ? (
        <p className="yt-admin-empty">No caption tracks yet.</p>
      ) : (
        <ul className="yt-cap-list">
          {captions.map((cap) => (
            <li key={cap.id} className="yt-cap-row">
              <span className="yt-cap-lang font-display-italic">
                {cap.language}
              </span>
              <span className="yt-cap-name">{cap.name || cap.trackKind}</span>
              <span className="yt-cap-flags">
                {cap.isAutoSynced && (
                  <span className="yt-row-privacy">auto</span>
                )}
                {cap.isDraft && (
                  <span className="yt-row-privacy">draft</span>
                )}
              </span>
              {!cap.isAutoSynced && (
                <button
                  type="button"
                  className="row-action is-danger"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete the ${cap.language} caption track? This cannot be undone.`,
                      )
                    ) {
                      del.mutate(cap.id);
                    }
                  }}
                  disabled={del.isPending}
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <form className="yt-cap-form mt-4" onSubmit={onSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-3 items-end">
          <label>
            <span className="auth-label">Language</span>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="auth-input"
            >
              {COMMON_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="auth-label">Track name (optional)</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="auth-input"
              placeholder="e.g. Sanskrit + IAST"
              maxLength={150}
            />
          </label>
          <label className="yt-admin-checkbox">
            <input
              type="checkbox"
              checked={isDraft}
              onChange={(e) => setIsDraft(e.target.checked)}
            />
            <span>Draft</span>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <label className="btn-app-ghost cursor-pointer">
            <span>{file ? file.name : "Choose .srt or .vtt"}</span>
            <input
              type="file"
              accept=".srt,.vtt,text/vtt,text/plain"
              className="sr-only"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <button
            type="submit"
            className="btn-app"
            disabled={!file || upload.isPending}
          >
            {upload.isPending ? "Uploading…" : "Upload track"}
          </button>
        </div>
        {error && (
          <p className="yt-admin-error mt-2" role="alert">
            {error}
          </p>
        )}
      </form>
    </section>
  );
}
